import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ShieldCheckIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import publicClient from '../../api/publicClient';

// Three checkboxes the candidate must confirm before they can take the survey.
// Worded to give them a graceful out: if any answer is no, we stop the flow
// and the apply lead is closed out without storing partial attestations.
const QUESTIONS = [
  { key: 'attestation_18plus',     label: 'I am at least 18 years old.' },
  { key: 'attestation_drug_test',  label: 'I am able to pass a drug test.' },
  { key: 'attestation_cps',        label: 'I am able to pass a CPS (Child Protective Services) background check.' },
];

function ResendLinkForm() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const { data } = await publicClient.post('/api/candidates/resend-link', { email: email.trim() });
      setSent(data.message);
    } catch (err) {
      // The endpoint is intentionally generic and only errors on missing
      // email or rate-limit. Surface the message so the candidate isn't stuck.
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-5 text-center">
        <p className="text-gray-200 text-sm">{sent}</p>
        <p className="text-gray-500 text-xs mt-2">Check your inbox (and spam folder).</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-5">
      <p className="text-sm text-gray-300 mb-3">Lost your link? Enter the email you applied with and we'll re-send it.</p>
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
        />
        <button
          type="submit"
          disabled={sending}
          className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold px-4 py-2.5 rounded-xl text-sm"
        >
          {sending ? 'Sending…' : 'Send link'}
        </button>
      </div>
    </form>
  );
}

export default function ContinueApplicationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embedded') === 'true';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    let cancelled = false;
    publicClient.get(`/api/continue/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setContext(data);
        // Hub routing: send the candidate to whichever step is next.
        // Attestation not done → stay here and show the form.
        // Done but no survey → /survey/:token
        // Survey done but no interview booked → /schedule/:token
        // All done → fall through to the "you're all set" view below.
        if (data.attestation_completed_at && !data.survey_completed_at) {
          navigate(`/survey/${token}`, { replace: true });
        } else if (data.attestation_completed_at && data.survey_completed_at && !data.interview_scheduled_at) {
          navigate(`/schedule/${token}`, { replace: true });
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'This link is invalid or expired.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, navigate]);

  const allYes = QUESTIONS.every(q => answers[q.key] === true);
  const anyNo = QUESTIONS.some(q => answers[q.key] === false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allYes) {
      toast.error('Please confirm all three before continuing.');
      return;
    }
    setSubmitting(true);
    try {
      await publicClient.post(`/api/continue/${token}`, {
        attestation_18plus: true,
        attestation_drug_test: true,
        attestation_cps: true,
      });
      navigate(`/survey/${token}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <div className="bg-red-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-12 h-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Link unavailable</h2>
            <p className="text-gray-400">{error}</p>
          </div>
          <ResendLinkForm />
        </div>
      </div>
    );
  }

  // Already-all-done view (everything completed AND interview booked).
  if (context?.attestation_completed_at && context?.survey_completed_at && context?.interview_scheduled_at) {
    const dt = new Date(context.interview_scheduled_at);
    const niceDate = dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const niceTime = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return (
      <div className={`min-h-screen bg-gray-900 ${embedded ? 'p-4' : 'p-6'} flex items-center justify-center`}>
        <Toaster position="top-right" />
        <div className="max-w-md w-full text-center">
          <div className="bg-green-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">You're all set, {context.first_name}.</h2>
          <p className="text-gray-300 mb-2">Interview confirmed for <strong>{niceDate}</strong> at <strong>{niceTime}</strong>.</p>
          <p className="text-gray-500 text-sm mt-3">Check your email for the calendar invite. We'll see you then!</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-900 ${embedded ? 'p-4' : 'p-6'}`}>
      <Toaster position="top-right" />
      <div className="max-w-lg mx-auto">
        {!embedded && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">BB</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Next Step</h1>
            <p className="text-gray-400 mt-1">Bright Beginnings Childcare</p>
          </div>
        )}

        <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                Hi {context?.first_name} — quick confirmations
              </h2>
              <p className="text-sm text-gray-400">All three must be true to move forward.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {QUESTIONS.map(q => (
              <div key={q.key} className="bg-gray-700/30 border border-gray-600/50 rounded-xl p-4">
                <p className="text-sm text-gray-200 mb-3">{q.label}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAnswers(prev => ({ ...prev, [q.key]: true }))}
                    className={`flex-1 rounded-lg py-2 text-sm font-medium border transition-colors ${
                      answers[q.key] === true
                        ? 'bg-green-500/20 border-green-400 text-green-300'
                        : 'bg-gray-800/50 border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnswers(prev => ({ ...prev, [q.key]: false }))}
                    className={`flex-1 rounded-lg py-2 text-sm font-medium border transition-colors ${
                      answers[q.key] === false
                        ? 'bg-red-500/20 border-red-400 text-red-300'
                        : 'bg-gray-800/50 border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}

            {anyNo && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="text-sm text-amber-200">
                  Thanks for letting us know. Unfortunately we can't move forward with applications that don't meet all three requirements. We appreciate your interest in Bright Beginnings.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!allYes || submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/30 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors mt-4 flex items-center justify-center gap-2"
            >
              <CheckCircleIcon className="w-5 h-5" />
              {submitting ? 'Saving…' : 'Continue to questionnaire'}
            </button>
          </form>
        </div>

        {!embedded && (
          <p className="text-center text-gray-600 text-sm mt-6">
            Bright Beginnings Childcare — Confidential
          </p>
        )}
      </div>
    </div>
  );
}
